import React, {useEffect, useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { PageLoading } from '../../Components/Loading';
import { useWhatsNew } from '../../Data/WhatsNew';
import Button from '../../Components/Button/Button';
import ButtonRow from '../../Components/Button/ButtonRow';
import TimeAgo from '../../Components/Formatting/TimeAgo';
import { useSession } from '../../Components/Session';
import Markdown from '../../Components/Formatting/Markdown';
import Money from '../../Components/Formatting/Money';
import { useBalance } from '../../Data/Balance';
import { useNavigate } from 'react-router-dom';
import { useDialogues } from '../../Components/Dialogues';
import { features } from '../../Data/Plans';
import Duration from '../../Components/Formatting/Duration';
import { secondsToDuration } from '../../Services/helpers';

const WhatsNew = () => {
    const {whatsNew, isSaving, isLoading, giveFeedback} = useWhatsNew(true);
    const {notifications, markMatchingNotificationsSeen} = useSession();

    useEffect(() => {
        markMatchingNotificationsSeen(n => n.entityModel === 'Soapy/WhatsNewNotification');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [whatsNew, notifications]);

    return <>
        <TopBar left="BackBtn" title="What's new" />
        <Page>
            {whatsNew ? <Inner {...{whatsNew, giveFeedback, isSaving, isLoading}} /> : <PageLoading />}
        </Page>
    </>
}

const Inner = ({whatsNew, giveFeedback, isSaving, isLoading}) => {
    return <div className="flex flex-col divide-y w-full">
        {whatsNew.map((item, i) => <Row key={i} {...item} {...{giveFeedback, isSaving, isLoading}} />)}
    </div>
}

const Row = ({id, title, description, announcedAt, url, cta, feature, feedback, giveFeedback, isSaving, isLoading}) => {
    const {hasFeature} = useSession();

    return <div className="flex flex-row justify-between py-5 first:pt-0">
        <div className="flex flex-col w-full">
            <div className="text-lg font-bold text-brand">{title}</div>
            <div className="text-sm text-muted">
                <TimeAgo dateTime={announcedAt} />
            </div>
            <div className="prose prose-sm prose-slate inline-doc">
                <Markdown markdown={description} />
            </div>
            <div className="flex flex-row justify-between">
                <Vote {...{id, feedback, giveFeedback, isSaving, isLoading}} />

                {url && <ButtonRow>
                    <Button theme="primary"
                        link={url}
                        label={cta ?? 'Try it out'}
                        disabled={feature && !hasFeature(feature)}
                    />
                </ButtonRow>}
            </div>
            {(feature && !hasFeature(feature)) && <UpgradePrompt {...{feature, announcedAt}} />}
        </div>
    </div>
}

const UpgradePrompt = ({feature, announcedAt}) => {
    const {balance, redeemVoucher} = useBalance();
    const navigate = useNavigate();
    const {confirm} = useDialogues();
    const [timeRemaining, setTimeRemaining] = useState(null);

    const hasUsed = balance?.vouchersUsed?.indexOf('WHATS-NEW') > -1;
    const announced = new Date(announcedAt);
    const expiryDate = new Date(announced.getTime() + 72 * 60 * 60 * 1000);
    const available = new Date().getTime() < expiryDate.getTime();

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeRemaining(
                Math.round(expiryDate.getTime() / 1000 - new Date().getTime() / 1000)
            );
        }, 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [announcedAt]);

    const expiringSoon = (timeRemaining > 0 && timeRemaining < 86400);

    if (!balance?.vouchersUsed) {
        return null;
    }

    if (hasUsed || !available) {
        return <div className="mt-2 bg-slate-100 rounded-xl border p-3 text-sm">
            <p className="font-bold text-brand">
            Want to try it out?
            </p>

            <p className="my-2">
                Level up your business with the {features[feature].toLowerCase()} on the Pro plan for <Money pence="1490" />/m
            </p>

            <ButtonRow>
                <Button theme="primary"
                    label="Compare plans"
                    link="/billing/plan"
                />
            </ButtonRow>
        </div>
    }

    return <div className="mt-2 bg-slate-100 rounded-xl border p-3 text-sm">
            <p className="font-bold text-brand">
                Want to try it out?
            </p>

            <p className="my-2">
                Try a full year of getSoapy Pro for just <Money pence="11980" className="font-bold" /> (save <Money pence={17880 - 11980} />)!
            </p>

            {!expiringSoon && <p className="my-2">
                This offer is only available for 72 hours!
            </p>}

            {expiringSoon && <p className="my-2 font-bold text-brand">
                This offer expires in <Duration time={secondsToDuration(timeRemaining)}
                    hideSeconds={timeRemaining > 60 * 30}
                />.
            </p>}

            <ButtonRow>
                <Button theme="primary"
                    label="Upgrade now"
                    onClick={() => {
                        confirm(
                            {
                                theme: 'primary',
                                title: 'Upgrade to Pro?',
                                message: 'You\'ll be charged £119.80 for a full year of getSoapy Pro. We\'ll take you to the billing page to complete the payment if you don\'t have a card on file.',
                                icon: 'RocketLaunch',
                                confirmLabel: 'Upgrade',
                            },
                            async () => {
                                await redeemVoucher('WHATS-NEW');
                                navigate('/billing');
                            }
                        )}
                    }
                />
            </ButtonRow>
        </div>
}

const Vote = ({id, feedback, giveFeedback, isSaving, isLoading}) => {
    const {up, down, user} = feedback;
    const [givenFeedback, setGivenFeedback] = useState(null);

    const doFeedback = (feedback) => {
        setGivenFeedback(feedback);
        giveFeedback(id, feedback);
    }

    return <div className="flex flex-row gap-2">
        <Button
            theme={user === -1 ? 'lightPrimary' : 'light'}
            loading={(isSaving || isLoading) && givenFeedback === -1}
            disabled={isSaving || isLoading}
            iconTheme={user === -1 ? 'solid' : 'light'}
            icon="ThumbsDown"
            label={down ? down : null}
            onClick={() => doFeedback(-1)}
        />
        <Button
            theme={user === 1 ? 'lightPrimary' : 'light'}
            loading={(isSaving || isLoading) && givenFeedback === 1}
            disabled={isSaving || isLoading}
            iconTheme={user === 1 ? 'solid' : 'light'}
            icon="ThumbsUp"
            label={up ? up : null}
            onClick={() => doFeedback(1)}
        />
    </div>
}

export default WhatsNew;
